type PatternParam<Pattern extends string> = Pattern extends `${infer Head}/${infer Tail}`
  ? PatternParam<Head> | PatternParam<Tail>
  : Pattern extends `:${infer Name}`
    ? Name
    : never;

const fillPattern = <Pattern extends string>(
  pattern: Pattern,
  params: Record<PatternParam<Pattern>, string>
): string => {
  const values: Readonly<Record<string, string>> = params;
  const segments = pattern
    .split('/')
    .map(segment => (segment.startsWith(':') ? encodeURIComponent(values[segment.slice(1)]) : segment))
    .filter(segment => segment !== '');
  return `/${segments.join('/')}`;
};

export const PASSCODE_ROUTES = {
  login: '/Login',
  legacyCodeLogin: '/login/code',
  legacyForgotPassword: '/ForgotPasswordV2',
  legacyResetPassword: '/resetPassword',
  legacyAcceptInvitation: '/acceptInvitation',
} as const;

export const LEGACY_LOGIN_PATHS: readonly string[] = [
  PASSCODE_ROUTES.legacyCodeLogin,
  PASSCODE_ROUTES.legacyForgotPassword,
  PASSCODE_ROUTES.legacyResetPassword,
  PASSCODE_ROUTES.legacyAcceptInvitation,
];

export const ONBOARDING_ROUTES = {
  wizard: '/onboarding',
  start: '/start',
} as const;

export const ACCOUNT_ROUTES = {
  contact: '/profile/contact',
  password: '/profile/password',
  root: '/profile',
  pictureAndDashboard: '/profile/picture',
} as const;

const CTL_BASE = '/profile/notifications';

export const CTL_PATTERNS = {
  plantPolicy: '/plants/:plantId/notification-policy',
  plantRouting: '/plants/:plantId/notification-routing',
  siteSettings: '/plants/:plantId/site-settings',
  landing: '/i/:token',
  legacyIssue: '/issues/:issueId',
} as const;

export const CTL_ROUTES = {
  settings: CTL_BASE,
  quietHours: `${CTL_BASE}/quiet-hours`,
  recent: `${CTL_BASE}/recent`,
  issuesList: '/tickets',
  plantPolicy: (plantId: string): string => fillPattern(CTL_PATTERNS.plantPolicy, { plantId }),
  plantRouting: (plantId: string): string => fillPattern(CTL_PATTERNS.plantRouting, { plantId }),
  siteSettings: (plantId: string): string => fillPattern(CTL_PATTERNS.siteSettings, { plantId }),
  landing: (token: string): string => fillPattern(CTL_PATTERNS.landing, { token }),
  issue: (issueId: string): string => fillPattern(CTL_PATTERNS.legacyIssue, { issueId }),
} as const;

const USER_CENTER_BASE = '/user-center';

export const USER_CENTER_ROUTES = {
  base: USER_CENTER_BASE,
  people: `${USER_CENTER_BASE}/people`,
  peopleAtPlant: (plantId: string): string =>
    `${USER_CENTER_BASE}/plants/${encodeURIComponent(plantId)}/people`,
  person: (userId: string): string => `${USER_CENTER_BASE}/people/${userId}`,
  library: `${USER_CENTER_BASE}/library`,
  plants: `${USER_CENTER_BASE}/plants`,
} as const;

const EQUIPMENT_LIBRARY_BASE = '/equipment-library';

export const EQUIPMENT_LIBRARY_SEGMENTS = {
  types: 'types',
  plantTypes: 'plant-types',
  scadaVocabulary: 'scada-vocabulary',
  readings: 'readings',
  equipmentCodes: 'equipment-codes',
  edit: 'edit',
  legacyDatabase: 'database',
} as const;

export const PLANT_EQUIPMENT_SEGMENTS = {
  sync: 'sync',
} as const;

export const EQUIPMENT_LIBRARY_PATTERNS = {
  root: EQUIPMENT_LIBRARY_BASE,
  types: `${EQUIPMENT_LIBRARY_BASE}/${EQUIPMENT_LIBRARY_SEGMENTS.types}`,
  type: `${EQUIPMENT_LIBRARY_BASE}/${EQUIPMENT_LIBRARY_SEGMENTS.types}/:code`,
  typeEdit: `${EQUIPMENT_LIBRARY_BASE}/${EQUIPMENT_LIBRARY_SEGMENTS.types}/:code/${EQUIPMENT_LIBRARY_SEGMENTS.edit}`,
  plantTypes: `${EQUIPMENT_LIBRARY_BASE}/${EQUIPMENT_LIBRARY_SEGMENTS.plantTypes}`,
  scadaVocabulary: `${EQUIPMENT_LIBRARY_BASE}/${EQUIPMENT_LIBRARY_SEGMENTS.scadaVocabulary}`,
  readings: `${EQUIPMENT_LIBRARY_BASE}/${EQUIPMENT_LIBRARY_SEGMENTS.scadaVocabulary}/${EQUIPMENT_LIBRARY_SEGMENTS.readings}`,
  equipmentCodes: `${EQUIPMENT_LIBRARY_BASE}/${EQUIPMENT_LIBRARY_SEGMENTS.scadaVocabulary}/${EQUIPMENT_LIBRARY_SEGMENTS.equipmentCodes}`,
} as const;

export const EQUIPMENT_LIBRARY_ROUTES = {
  root: EQUIPMENT_LIBRARY_PATTERNS.root,
  types: EQUIPMENT_LIBRARY_PATTERNS.types,
  plantTypes: EQUIPMENT_LIBRARY_PATTERNS.plantTypes,
  scadaVocabulary: EQUIPMENT_LIBRARY_PATTERNS.scadaVocabulary,
  readings: EQUIPMENT_LIBRARY_PATTERNS.readings,
  equipmentCodes: EQUIPMENT_LIBRARY_PATTERNS.equipmentCodes,
  type: (code: string): string => fillPattern(EQUIPMENT_LIBRARY_PATTERNS.type, { code }),
  typeEdit: (code: string): string => fillPattern(EQUIPMENT_LIBRARY_PATTERNS.typeEdit, { code }),
} as const;

export const RESERVED_LIBRARY_SEGMENTS: readonly string[] = [
  EQUIPMENT_LIBRARY_SEGMENTS.types,
  EQUIPMENT_LIBRARY_SEGMENTS.plantTypes,
  EQUIPMENT_LIBRARY_SEGMENTS.scadaVocabulary,
  EQUIPMENT_LIBRARY_SEGMENTS.legacyDatabase,
];

const RESERVED_LOWER_CASE: ReadonlySet<string> = new Set(RESERVED_LIBRARY_SEGMENTS.map((segment) => segment.toLowerCase()));

export const isReservedLibrarySegment = (code: string): boolean => RESERVED_LOWER_CASE.has(code.toLowerCase());

const PLANT_EQUIPMENT_BASE = '/plants/:plantId/equipment';

export const PLANT_EQUIPMENT_PATTERNS = {
  machines: PLANT_EQUIPMENT_BASE,
  machine: `${PLANT_EQUIPMENT_BASE}/:entryId`,
  sync: `${PLANT_EQUIPMENT_BASE}/${PLANT_EQUIPMENT_SEGMENTS.sync}`,
  legacyScada: '/plants/:plantId/equipment-library/scada',
} as const;

export const PLANT_EQUIPMENT_ROUTES = {
  machines: (plantId: string): string => fillPattern(PLANT_EQUIPMENT_PATTERNS.machines, { plantId }),
  machine: (plantId: string, entryId: string): string => fillPattern(PLANT_EQUIPMENT_PATTERNS.machine, { plantId, entryId }),
  sync: (plantId: string): string => fillPattern(PLANT_EQUIPMENT_PATTERNS.sync, { plantId }),
} as const;
